/**
 * `tarii.product_invite` — the invite code a visitor arrived with (#2652).
 *
 * `/start?invite=<code>` stores the code in `localStorage` and tells the API
 * ("redeem"). The anon browser id lets the API tie a signed-out redemption to
 * this browser, and lets the sign-in claim it without using a second slot.
 * The store and the `invite=` query on product calls both come from here.
 */
import { apiFetch } from "../api/client";

export const PRODUCT_INVITE_KEY = "tarii.product_invite";
export const PRODUCT_INVITE_ANON_KEY = "tarii.product_invite_anon";

export const INVITE_USED_LINE = "This invite link has been used or has expired.";

function read(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the code still works for this page load.
  }
}

export function storedInviteCode(): string | null {
  return read(PRODUCT_INVITE_KEY);
}

/** Fired when the stored code or a redemption changes, so offers refetch. */
export const INVITE_CHANGED_EVENT = "tarii:product-invite-changed";

function announce(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(INVITE_CHANGED_EVENT));
}

export function storeInviteCode(code: string): void {
  write(PRODUCT_INVITE_KEY, code);
  announce();
}

export function clearInviteCode(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PRODUCT_INVITE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** A random id for this browser, made once. */
export function anonId(): string {
  const have = read(PRODUCT_INVITE_ANON_KEY);
  if (have) return have;
  const made =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  write(PRODUCT_INVITE_ANON_KEY, made);
  return made;
}

/** Query string for `/api/products` calls: `?invite=…&anon=…`, or "". */
export function inviteQuery(): string {
  const code = storedInviteCode();
  if (!code) return "";
  return `?invite=${encodeURIComponent(code)}&anon=${encodeURIComponent(anonId())}`;
}

/** Query string for `POST /api/teams`: `?invite=…`, or "". */
export function teamInviteQuery(): string {
  const code = storedInviteCode();
  return code ? `?invite=${encodeURIComponent(code)}` : "";
}

/** Redeem a code. Resolves to the product key, or null when the API refuses it. */
const inFlight = new Map<string, Promise<string | null>>();

export function redeemInvite(code: string): Promise<string | null> {
  // One call at a time per code: two effects must not spend two slots.
  const key = code;
  const running = inFlight.get(key);
  if (running) return running;
  const call = apiFetch<{ product_key: string }>(
    `/api/products/invites/${encodeURIComponent(code)}/redeem`,
    { method: "POST", body: JSON.stringify({ anon_id: anonId() }) },
  )
    .then((res) => res.product_key as string | null)
    .catch(() => null)
    .finally(() => {
      inFlight.delete(key);
      announce();
    });
  inFlight.set(key, call);
  return call;
}
